import { computed, watch, type Ref } from 'vue'
import type { GemsApi_StoneCategory } from '@/graphql/api'
import type { RingBuilderStoneTypes } from '@/domain/product/ring/ring-builder'

import type { Bounds } from 'wucdbm-exponential-slider'
import {
    getStoneSubtitle,
    getStoneTitle,
} from '@/components/stone/getStoneTitles'
import { useSegment } from '@/third-party/segment.ts'

type RecommendationsAnalyticsPrice = {
    min: number | null
    max: number | null
}

export type RecommendationsAnalyticsOption =
    | 'Best option for size'
    | 'Best option for price'
    | 'Best option for balance'
    | 'Gemstone option'

export function useRecommendationsAnalytics(
    category: Readonly<Ref<GemsApi_StoneCategory>>,
    bounds: Readonly<Ref<Bounds | null>>
): {
    trackStoneSelected: (
        stoneData: RingBuilderStoneTypes,
        option: RecommendationsAnalyticsOption
    ) => void
    trackOwnStoneSearch: () => void
} {
    const { track } = useSegment()

    const price = computed(
        () =>
            bounds.value || {
                min: null,
                max: null,
            }
    )

    const trackStonePriceChanged = (price: RecommendationsAnalyticsPrice) => {
        track('rec_stone_price_changed', price)
    }

    watch(price, (price) => trackStonePriceChanged(price))

    const trackStoneCategorySelected = (category: GemsApi_StoneCategory) => {
        track('rec_stone_category_selected', { category })
    }

    watch(category, (category) => trackStoneCategorySelected(category))

    const trackStoneSelected = (
        stoneData: RingBuilderStoneTypes,
        option: RecommendationsAnalyticsOption
    ) => {
        const stone = `${getStoneTitle(stoneData)}, ${getStoneSubtitle(
            stoneData
        )}`

        track('rec_stone_selected', {
            stone,
            ...price.value,
            category: category.value,
            option,
        })
    }

    const trackOwnStoneSearch = () => {
        track('rec_own_stone_search')
    }

    // Handled in useThButton.ts as the CTA comes from the CMS
    // const trackExpertNeededClick = () => {
    //     track('rec_expert_needed_click')
    // }

    return {
        trackStoneSelected,
        trackOwnStoneSearch,
    }
}
